import { clsx } from 'clsx';
import ImgCrop from 'antd-img-crop';
import { Button } from '@mui/material';
import { useController } from 'react-hook-form';
import type { ColumnsType } from 'antd/es/table';
import type { RcFile, UploadProps } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { Col, message, Row, Table, Upload, Input, Select } from 'antd';
import { DeleteButton, SaveButton, SimpleForm, TopToolbar } from 'react-admin';
import React, {
  useEffect,
  useState,
  Key,
  ChangeEvent,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react';

import { useRbacEdit } from '@hooks';
import { isProdServer, unAccent } from '@utils';
import { ExportMktResponse, productHttp } from '@network';
import { Container, SwitchControlled } from '@components';
import { ConfirmDelete } from '@components/ConfirmDelete';
import { ImportCSV } from '@pages/dealer/Marketplace/ImportCSV';
import { Dealer, Product, ProductBrand, ProductDivision, ProductType } from '@types';
import cls from './Marketplace.module.css';

const { Search } = Input;
const MAX_FILE_SIZE = 3;

interface Props {
  dealer: Dealer;
}

interface FilterProps {
  q: string;
  brand?: any;
  category?: any;
  subcategory?: any;
  enabled?: boolean;
}

interface ExportProps {
  dealerId: number;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  result: ExportMktResponse;
  setResult: Dispatch<SetStateAction<ExportMktResponse>>;
  getList: () => Promise<Product[]>;
}

interface TableCmpProps {
  dealer: Dealer;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  result: ExportMktResponse;
  setResult: Dispatch<SetStateAction<ExportMktResponse>>;
  productsList: Product[];
  filteredProductsList: Product[];
  setFilteredProductsList: Dispatch<SetStateAction<Product[]>>;
  getList: () => Promise<Product[]>;
}

const Actions = () => {
  return (
    <TopToolbar className={cls.actions}>
      <DeleteButton className={clsx(cls.action, cls.actionDelete)} />

      <SaveButton
        label="Save"
        type="button"
        className={clsx(cls.action, cls.actionSave)}
        variant="outlined"
      />
    </TopToolbar>
  );
};

const ExportIcon = () => (
  <svg className={cls.exportIcon}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <rect width="20" height="20" rx="4" fill="#042E6B" />
    <path d="M16 13.4118C15.9999 14.2879 15.3274 15 14.5 15H5.5C4.67264 14.9999 4.00006 14.2878 4 13.4118V7.58824C4 6.71214 4.6726 6.0001 5.5 6H7.5C7.77499 6 8 6.23824 8 6.52941C8 6.82058 7.77499 7.05882 7.5 7.05882H5.5C5.22509 7.05893 5 7.29713 5 7.58824V13.4118C5.00006 13.7028 5.22513 13.9411 5.5 13.9412H14.5C14.775 13.9412 14.9999 13.7029 15 13.4118V7.58824C15 7.29706 14.775 7.05882 14.5 7.05882H12.5C12.225 7.05879 12 6.82056 12 6.52941C12 6.23826 12.225 6.00003 12.5 6H14.5C15.3275 6 16 6.71207 16 7.58824V13.4118Z"
      fill="white" />
    <path d="M12.2977 9.33266C12.5077 9.52325 12.5325 9.85678 12.3525 10.0791L10.3525 12.1968C10.1575 12.4031 9.84246 12.4032 9.64751 12.1968L7.64796 10.0791C7.62796 10.0606 7.61026 10.0418 7.59276 10.0207C7.41284 9.79831 7.438 9.46477 7.64796 9.27421C7.85796 9.08381 8.173 9.11038 8.35294 9.33266L9.50045 10.5448V6.52941C9.50045 6.23832 9.7251 6.00013 10 6C10.275 6 10.5 6.23824 10.5 6.52941V10.5448L11.6475 9.33266C11.835 9.16325 12.1102 9.16325 12.2977 9.33266Z"
      fill="white" />
  </svg>
);

const EarthIcon = () => (
  <svg className={cls.earthtIcon}
    width="38"
    height="38"
    viewBox="0 0 38 38"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <circle cx="19" cy="19" r="19" fill="#042E6B" />
    <g clipPath="url(#clip0_5422_32939)">
      <path d="M19 5C11.3 5 5 11.3 5 19C5 26.7 11.3 33 19 33C26.7 33 33 26.7 33 19C33 11.3 26.7 5 19 5ZM31.0867 18.0667H25.3C25.2533 16.2933 25.0667 14.6133 24.7867 13.12C26.3267 12.84 27.68 12.42 28.8467 11.9067C30.1067 13.68 30.9 15.78 31.0867 18.0667ZM27.54 10.4133C26.6067 10.7867 25.5333 11.0667 24.32 11.3C23.9 9.76 23.2933 8.45333 22.64 7.42667C24.5067 8.03333 26.1867 9.06 27.54 10.4133ZM14.5667 18.0667C14.6133 16.34 14.8 14.8 15.08 13.4C16.2933 13.54 17.6 13.6333 19 13.6333C20.4 13.6333 21.66 13.54 22.92 13.4C23.2 14.8 23.3867 16.34 23.4333 18.0667H14.5667ZM23.4333 19.9333C23.3867 21.66 23.2 23.2 22.92 24.6C21.7067 24.46 20.4 24.3667 19 24.3667C17.6 24.3667 16.34 24.46 15.08 24.6C14.8 23.2 14.6133 21.66 14.5667 19.9333H23.4333ZM19 6.86667C20.26 6.86667 21.5667 8.64 22.4533 11.58C21.38 11.72 20.2133 11.7667 19 11.7667C17.7867 11.7667 16.62 11.6733 15.5467 11.58C16.4333 8.64 17.74 6.86667 19 6.86667ZM15.36 7.42667C14.7067 8.45333 14.1 9.76 13.68 11.3C12.4667 11.0667 11.3933 10.7867 10.46 10.4133C11.8133 9.06 13.4933 8.03333 15.36 7.42667ZM9.15333 11.9067C10.32 12.42 11.6733 12.84 13.2133 13.12C12.9333 14.6133 12.7467 16.2933 12.7 18.0667H6.91333C7.1 15.78 7.89333 13.68 9.15333 11.9067ZM6.91333 19.9333H12.7C12.7467 21.7067 12.9333 23.3867 13.2133 24.88C11.6733 25.16 10.32 25.58 9.15333 26.0933C7.89333 24.3667 7.1 22.22 6.91333 19.9333ZM10.46 27.5867C11.3933 27.2133 12.4667 26.9333 13.68 26.7C14.1 28.24 14.7067 29.5467 15.36 30.5733C13.4933 29.9667 11.8133 28.94 10.46 27.5867ZM19 31.1333C17.74 31.1333 16.4333 29.36 15.5467 26.42C16.62 26.28 17.7867 26.2333 19 26.2333C20.2133 26.2333 21.38 26.3267 22.4533 26.42C21.5667 29.36 20.26 31.1333 19 31.1333ZM22.64 30.5733C23.2933 29.5467 23.9 28.24 24.32 26.7C25.5333 26.9333 26.6067 27.2133 27.54 27.5867C26.1867 28.94 24.5067 29.9667 22.64 30.5733ZM28.8467 26.0933C27.68 25.58 26.3267 25.16 24.7867 24.88C25.0667 23.3867 25.2533 21.7067 25.3 19.9333H31.0867C30.9 22.22 30.1067 24.32 28.8467 26.0933Z"
        fill="white" />
    </g>
    <defs>
      <clipPath id="clip0_5422_32939">
        <rect width="28" height="28" fill="white" transform="translate(5 5)" />
      </clipPath>
    </defs>
  </svg>
);

const BoolIcon = ({ color = '#0C5541' }: {color?: string}) => (
  <svg width="1em" height="1em" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="10" fill={color} />
    <path d="M4.73301 9.12203C5.13784 8.74635 5.77056 8.76999 6.14624 9.17482L8.86711 12.1069L13.9982 7.34533C14.403 6.96966 15.0357 6.9933 15.4114 7.39813C15.7871 7.80296 15.7634 8.43568 15.3586 8.81135L9.49454 14.2531C9.11507 14.6052 8.53518 14.6068 8.15464 14.2721C8.1292 14.2498 8.10485 14.2257 8.08131 14.2003L4.68022 10.5353C4.30454 10.1304 4.32818 9.4977 4.73301 9.12203Z"
      fill="white" />
  </svg>
);

const LoadStatus = () => (
  <div className={clsx(cls.status, cls.loadingStatus)}>
    <LoadingOutlined className={cls.statusIcon} /> Loading
  </div>
);

const ReadyStatus = () => (
  <div className={clsx(cls.status, cls.readyStatus)}>
    <CheckOutlined className={cls.statusIcon} /> Ready
  </div>
);

const Status = (
  { result, loading }: {result: ExportMktResponse, loading: boolean},
) => {
  if (result && (result.listed !== null && result.unknown !== null && result.enabled !== null)) {
    return <ReadyStatus />;
  }
  if (loading) {
    return <LoadStatus />;
  }
  return <></>;
};

const Header = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const {
    field: { value: mktLogo, onChange: changeMktLogo },
  } = useController({ name: 'mktLogo' });
  const {
    field: { value: mktUrl, onChange: changeMktUrl },
  } = useController({ name: 'mktUrl' });
  const {
    field: { value: mktOnline, onChange: changeMktOnline },
  } = useController({ name: 'mktOnline' });

  const clearMktLogo = () => {
    setFileList([]);
    changeMktLogo(null);
  };

  const onImgChange: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
    const normalizedFileList = newFileList.slice(-1);
    setFileList(normalizedFileList);

    const file: UploadFile | undefined = normalizedFileList[0];
    if (!file) return;

    if (!file.originFileObj) {
      changeMktLogo(file.url || '');
      return;
    }

    const resizeImage = (file: RcFile, width: number, height: number): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const img = new Image();
          img.src = reader.result as string;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject('Canvas error');
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL(file.type));
          };
          img.onerror = err => reject(err);
        };
        reader.onerror = err => reject(err);
      });
    };

    try {
      const src = await resizeImage(file.originFileObj as RcFile, 600, 200);
      changeMktLogo(src);
    } catch (err) {
      message.error('File error');
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng =
      file.type === 'image/jpeg' || file.type === 'image/png';

    if (!isJpgOrPng) {
      message.error('Only JPG/PNG');
      return Upload.LIST_IGNORE;
    }

    const isValidSize = file.size / 1024 / 1024 < MAX_FILE_SIZE;

    if (!isValidSize) {
      message.error(`File bigger than ${MAX_FILE_SIZE}MB`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const onUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    const mktUrl = e.target.value.toLowerCase()
      .replace(/[^a-z0-9-]+/g, '')
      .replace(/(^|-)([0-9]+)/g, '$1')
      .slice(0, 150);
    changeMktUrl(mktUrl);
  };

  return (
    <div className={cls.headerBlock}>
      <div className={cls.headerRow}>
        <div className={cls.headerCol}>
          <div className={cls.headerImgDesc}>
            <div className={cls.headerImgDescTxt}>Marketplace Logo:</div>
            <div className={cls.headerImgDescTxt}>(Up to {MAX_FILE_SIZE} Mb)</div>
            <div className={cls.headerImgDescTxt}>300x100</div>
          </div>
          <div className={cls.headerImg}>
            {mktLogo ? (
              <div className={cls.previewBlock}>
                <ImgCrop aspect={3} quality={1}>
                  <Upload
                    customRequest={async ({ onSuccess }) => setTimeout(() => {
                      onSuccess && onSuccess('ok');
                    }, 0)}
                    showUploadList={false}
                    maxCount={1}
                    fileList={fileList}
                    beforeUpload={beforeUpload}
                    onChange={onImgChange}
                  >
                    <button type="button" className={cls.previewUploadBtn}>
                      <img alt="preview" className={cls.preview} src={mktLogo} />
                    </button>
                  </Upload>
                </ImgCrop>
                <div className={cls.logoRemove}>
                  <ConfirmDelete title="Are you sure?" cb={clearMktLogo}>
                    <svg xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none">
                      <path d="M9 0C4.03021 0 0 4.03021 0 9C0 13.9698 4.03021 18 9 18C13.9698 18 18 13.9698 18 9C17.992 4.03021 13.9658 0.00399822 9 0Z"
                        fill="#002A77" />
                      <path d="M12.3863 5.60962C12.2303 5.45369 11.9785 5.45369 11.8225 5.60962L8.99978 8.43237L6.17704 5.60962C6.0211 5.45369 5.76922 5.45369 5.61329 5.60962C5.45736 5.76555 5.45736 6.01744 5.61329 6.17337L8.43603 8.99612L5.61329 11.8189C5.45736 11.9748 5.45736 12.2267 5.61329 12.3826C5.76922 12.5385 6.0211 12.5385 6.17704 12.3826L8.99978 9.55987L11.8225 12.3826C11.9785 12.5385 12.2303 12.5385 12.3863 12.3826C12.5422 12.2267 12.5422 11.9748 12.3863 11.8189L9.56353 8.99612L12.3863 6.17337C12.5422 6.01744 12.5422 5.76555 12.3863 5.60962Z"
                        fill="white" />
                    </svg>
                  </ConfirmDelete>
                </div>
              </div>
            ) : (
              <ImgCrop aspect={3} quality={1}>
                <Upload
                  customRequest={async ({ onSuccess }) => setTimeout(() => {
                    onSuccess && onSuccess('ok');
                  }, 0)}
                  listType="picture-card"
                  maxCount={1}
                  fileList={fileList}
                  beforeUpload={beforeUpload}
                  onChange={onImgChange}
                  className={cls.imgUploader}
                  prefixCls={cls.imgPrefixUploader}
                >
                  <div className={cls.imgUploadBtn}>+</div>
                </Upload>
              </ImgCrop>
            )}
          </div>
        </div>
        <div className={cls.headerCol}>
          <div className={cls.headerUrlTop}>
            <div className={cls.headerUrl}>
              <div className={cls.headerUrlLabel}>Url</div>
              <div className={cls.headerUrlInput}>
                <input
                  className={cls.headerUrlInputField}
                  onChange={onUrlChange}
                  value={mktUrl} />
              </div>
            </div>
            <div className={cls.switcher}>
              <SwitchControlled
                value={mktOnline}
                onChange={changeMktOnline} />
              <div className={cls.switcherLabel}>Online</div>
            </div>
          </div>
          <div className={cls.headerUrlBottom}>
            <div className={cls.headerUrlInfo}>
              <div className={cls.headerUrlicon}>
                <EarthIcon />
              </div>
              <div className={cls.headerUrlText}>
                {`https://${!isProdServer() ? 'dev.' : ''}verqor.com/partners/${mktUrl ? mktUrl : ''}`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TableBlock = ({
  dealer, loading, setLoading, setResult, result, productsList,
  filteredProductsList, setFilteredProductsList, getList,
}: TableCmpProps) => {
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const {
    field: { value: productsIds, onChange: changeProductsIds },
  } = useController({ name: 'productsIds' });
  const [selectedProductsIdsList, setSelectedProductsIdsList] = useState<Key[]>([]);
  const [brands, setBrands] = useState<ProductBrand[]>([]);
  const [categories, setCategories] = useState<ProductBrand[]>([]);
  const [subcategories, setSubcategories] = useState<ProductBrand[]>([]);
  const [filters, setFilters] = useState<FilterProps>({
    q: '',
    brand: undefined,
    category: undefined,
    subcategory: undefined,
    enabled: undefined,
  });
  const columns = useMemo<ColumnsType<Product>>(() => [
    {
      title: 'Name',
      dataIndex: 'name',
      width: 680,
      sorter: (a: Product, b: Product) => `${a.name}`.localeCompare(`${b.name}`),
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      width: 280,
      sorter: (a, b) =>
        safeString(a.brand?.name).localeCompare(safeString(b.brand?.name)),
      render: (brand: ProductBrand | null) => brand?.name || '-',
    },
    {
      title: 'Category',
      dataIndex: 'division',
      width: 280,
      sorter: (a, b) =>
        safeString(a.division?.name).localeCompare(safeString(b.division?.name)),
      render: (division: ProductDivision | null) => division?.name || '-',
    },
    {
      title: 'Subcategory',
      dataIndex: 'type',
      width: 280,
      sorter: (a, b) =>
        safeString(a.type?.name).localeCompare(safeString(b.type?.name)),
      render: (type: ProductType | null) => type?.name || '-',
    },
    {
      title: 'Stock',
      dataIndex: 'stock',
      width: 80,
      sorter: (a, b) => (+a - +b),
      render: (num: number | null) => num ?? '-',
    },
    {
      title: 'On MKTplace',
      dataIndex: 'dealersIds',
      width: 100,
      align: 'center',
      render: (dealersIds?: number[]) => {
        const enabled = dealersIds?.includes(dealer?.id);
        return (
          <span className={cls.boolWrap} key={enabled ? 'on' : 'off'}>
            <BoolIcon color={enabled ? undefined : '#74706B7A'} />
          </span>
        );
      },
    },
  ], [dealer.productsIds]);

  const safeString = (value?: string | null) => value ?? '';

  const getBrands = async (): Promise<void> => {
    try {
      const data = await productHttp.getBrandsList();
      data && setBrands(data);
    } catch (e) {
      console.error(e);
    }
  };

  const getCategoryList = async (): Promise<void> => {
    try {
      const data = await productHttp.getCategoryList();
      data && setCategories(data);
    } catch (e) {
      console.error(e);
    }
  };

  const getSubcategoryList = async (): Promise<void> => {
    try {
      const data = await productHttp.getSubcategoryList();
      data && setSubcategories(data);
    } catch (e) {
      console.error(e);
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedProductsIdsList,
    onChange: (selectedRowKeys: Key[]) => {
      setSelectedProductsIdsList(selectedRowKeys);
    },
  };

  const onBulkUpdate = (val = true): void => {
    if (!selectedProductsIdsList.length) return;

    const newProductsIds: Key[] = val
      ? [...new Set([...(productsIds || []), ...selectedProductsIdsList])]
      : (productsIds || []).filter(p => !selectedProductsIdsList.includes(p));

    changeProductsIds(newProductsIds);

    setFilteredProductsList(prev =>
      prev.map(product => {
        if (selectedProductsIdsList.includes(product.id)) {
          const currentDealers = product.dealersIds || [];
          const newDealers = val
            ? Array.from(new Set([...currentDealers, dealer.id]))
            : currentDealers.filter(dId => dId !== dealer.id);

          return { ...product, dealersIds: newDealers };
        }
        return product;
      }),
    );

    setSelectedProductsIdsList([]);
  };

  const filterProducts = () => {
    const fList = productsList.filter(item => {
      if (filters.q.trim() && !item.name.toLowerCase().includes(filters.q.trim().toLowerCase())) {
        return false;
      }

      if (Array.isArray(filters.brand)
        && filters.brand.length > 0
        && !filters.brand.includes(item.brandId)
      ) {
        return false;
      }

      if (Array.isArray(filters.category)
        && filters.category.length > 0
        && !filters.category.includes(item.categoryId)
      ) {
        return false;
      }

      if (Array.isArray(filters.subcategory)
        && filters.subcategory.length > 0
        && !filters.subcategory.includes(item.subcategoryId)
      ) {
        return false;
      }

      if (filters.enabled !== undefined) {
        if (filters.enabled) {
          return item.dealersIds?.includes(dealer?.id);
        } else {
          return !item.dealersIds?.includes(dealer?.id);
        }
      }

      return true;
    });

    setFilteredProductsList(fList);
  };

  useEffect(() => {
    (async () => {
      await Promise.all([
        getList(),
        getBrands(),
        getSubcategoryList(),
        getCategoryList(),
      ]);
    })();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [filters]);

  useEffect(() => {
    setFilteredProductsList(prev => prev.map(p => ({ ...p })));
  }, [dealer]);

  console.log({ filteredProductsList });

  return (
    <div className={cls.tableBlock}>
      <div className={cls.tableHeaderRow}>
        <div className={cls.tableHeaderCol}>
          <div className={cls.tableHeaderTitle}>Marketplace Products</div>
        </div>
        <div className={cls.tableHeaderCol}>
          <div className={cls.tableFilters}>
            <Search
              placeholder="Search"
              allowClear
              className={cls.searchFilter}
              onChange={(e) => setFilters(prev => ({ ...prev, q: e.target.value }))} />

            <Select
              placeholder="Brand"
              onChange={id => setFilters(prev => ({ ...prev, brand: id }))}
              value={filters.brand}
              className={cls.selectFilter}
              showSearch
              allowClear
              mode="tags"
              options={brands.map(o => ({ value: o.id, label: o.name }))}
              filterOption={(input, option) =>
                `${unAccent(option?.label)}`.toLowerCase().includes(unAccent(input).toLowerCase())}
            />

            <Select
              placeholder="Category"
              onChange={id => setFilters(prev => ({ ...prev, category: id }))}
              value={filters.category}
              className={cls.selectFilter}
              showSearch
              allowClear
              mode="tags"
              options={categories.map(o => ({ value: o.id, label: o.name }))}
              filterOption={(input, option) =>
                `${unAccent(option?.label)}`.toLowerCase().includes(unAccent(input).toLowerCase())}
            />

            <Select
              placeholder="Subcategory"
              onChange={id => setFilters(prev => ({ ...prev, subcategory: id }))}
              value={filters.subcategory}
              className={cls.selectFilter}
              showSearch
              allowClear
              mode="tags"
              options={subcategories.map(o => ({ value: o.id, label: o.name }))}
              filterOption={(input, option) =>
                `${unAccent(option?.label)}`.toLowerCase().includes(unAccent(input).toLowerCase())}
            />

            <Select
              placeholder="On MKTplace"
              onChange={val => setFilters(prev => ({ ...prev, enabled: val }))}
              value={filters.enabled}
              className={cls.selectFilter}
              showSearch
              allowClear
              options={[
                { value: true, label: 'Enabled' },
                { value: false, label: 'Disabled' },
              ]}
            />

            <ImportCSV
              placement="bottom"
              loading={loading}
              setLoading={setLoading}
              setResult={setResult}
              disabled={result.listed !== null}
              onSuccess={getList}
              dealerId={dealer.id} />
          </div>
        </div>
      </div>

      <div className={cls.tableWrap}>
        {selectedProductsIdsList.length ? (
          <div className={cls.tableBulk}>
            <div className={cls.tableBulkRow}>
              <div className={cls.tableBulkCol}>
                <button className={cls.bulkBtn} onClick={() => onBulkUpdate()}>
                  <BoolIcon /> <span className={cls.bulkText}>Enable</span>
                </button>
              </div>
              <div className={cls.tableBulkCol}>
                <button className={cls.bulkBtn} onClick={() => onBulkUpdate(false)}>
                  <BoolIcon color="#74706B7A" /> <span className={cls.bulkText}>Disable</span>
                </button>
              </div>
              <div className={cls.tableBulkCol}>
                <span className={cls.bulkText}>{selectedProductsIdsList.length} items</span>
              </div>
            </div>
          </div>
        ) : null}

        <Table
          className={cls.table}
          rowClassName={cls.tableRow}
          columns={columns}
          dataSource={filteredProductsList || []}
          rowKey="id"
          rowSelection={rowSelection}
          loading={loading}
          showSorterTooltip={false}
          pagination={{
            className: cls.tablePagination,
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredProductsList.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            locale: {
              items_per_page: '',
            },
            position: ['bottomLeft'],
            size: 'small',
            showTotal: (total, range) =>
              `${range[0]} - ${range[1]} of ${total}`,
          }}
          onChange={(paginationConfig) => {
            setPagination({
              current: paginationConfig.current || 1,
              pageSize: paginationConfig.pageSize || 10,
            });
          }}
        />
      </div>
    </div>
  );
};

const Footer = ({
  dealerId, loading, setLoading, setResult, result, getList,
}: ExportProps) => {
  const downloadCsv = (csv: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'unknown_products.csv';
    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className={cls.footerBlock}>
      <div className={cls.footerRow}>
        <div className={cls.footerCol}>
          <div className={cls.footerTitle}>
            CSV Export Response <Status result={result} loading={loading} />
          </div>
          <div className={cls.footerList}>
            <div className={cls.footerContentRow}>
              <div className={cls.footerContentLabel}>Products Listed</div>
              <div className={cls.footerContentValue}>
                <div className={cls.footerContentText}>
                  {!result?.listed && result.listed !== 0 ? '-' : result.listed}
                </div>
                <div className={cls.footerContentPostfix}></div>
              </div>
            </div>
            <div className={cls.footerContentRow}>
              <div className={cls.footerContentLabel}>Products Enabled</div>
              <div className={cls.footerContentValue}>
                <div className={cls.footerContentText}>
                  {!result?.enabled && result.enabled !== 0 ? '-' : result.enabled}
                </div>
                <div className={cls.footerContentPostfix}></div>
              </div>
            </div>
            <div className={clsx(cls.footerContentRow, cls.alert)}>
              <div className={cls.footerContentLabel}>Unknown Products (not enabled)</div>
              <div className={cls.footerContentValue}>
                <div className={cls.footerContentText}>
                  {!result?.unknown && result.unknown !== 0 ? '-' : result.unknown}
                </div>
                <div className={cls.footerContentPostfix}>
                  <Button
                    className={cls.exportBtn}
                    disabled={!result?.unknown || result.unknown === 0}
                    onClick={() => result.csv ? downloadCsv(result.csv) : undefined}
                  ><ExportIcon /></Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={cls.footerCol}>
          <div className={cls.footerSubTitle}>Instructions for CSV Export:</div>
          <ol className={cls.ol}>
            <li className={cls.olItem}>
              Download this format:&nbsp;
              <a className={cls.footerLink} href="products.csv" download>FILE</a>
            </li>
            <li className={cls.olItem}>
              Complete the columns with only the products you want to enable
            </li>
            <li className={cls.olItem}>Upload the CSV</li>
          </ol>
        </div>
        <div className={cls.footerCol}>
          <div className={cls.footerSubTitle}>
            Rules to Normalize data for CSV interpretation
          </div>
          <ol className={cls.ol}>
            <li className={cls.olItem}>All High Caps</li>
            <li className={cls.olItem}>Remove Spaces “ “</li>
            <li className={cls.olItem}>Remove Special Characters and punctuation</li>
            <li className={cls.olItem}>Remove Duplicates</li>
          </ol>
        </div>
      </div>
      <div className={cls.footerActions}>
        <ImportCSV
          loading={loading}
          setLoading={setLoading}
          placement="top"
          dealerId={dealerId}
          setResult={setResult}
          onSuccess={getList}
          disabled={result.unknown !== null}
        />
        <Button
          className={clsx(cls.footerBtn, cls.footerBtnOutline, cls.restartBtn)}
          variant="outlined"
          disabled={result.unknown === null || loading}
          onClick={() => setResult({
            listed: null,
            enabled: null,
            unknown: null,
            csv: null,
          })}
        >Start Again</Button>
      </div>
    </div>
  );
};

export const Marketplace = ({ dealer }: Props) => {
  const rbacEdit = useRbacEdit();
  const [loading, setLoading] = useState(false);
  const [productsList, setProductsList] = useState<Product[]>([]);
  const [filteredProductsList, setFilteredProductsList] = useState<Product[]>([]);
  const [exportResult, setExportResult] = useState<ExportMktResponse>({
    listed: null,
    enabled: null,
    unknown: null,
    csv: null,
  });

  const getList = async (): Promise<Product[]> => {
    let result: Product[] = [];

    if (dealer.manager) {
      setLoading(true);
      const data = await productHttp.getAllList({
        isExternal: true,
        _start: 0,
        _end: 9999999,
        _order: 'ASC',
        _sort: 'name',
        marketplaceDealerId: dealer.id,
      });
      if (data?.length) {
        setProductsList(data);
        setFilteredProductsList(data);
        result = data;
      }
      setLoading(false);
    }

    return result;
  };

  return (
    <SimpleForm
      toolbar={rbacEdit ? <Actions /> : false}
      className={clsx(cls.form, 'manager-form')}
      mode="onChange"
    >
      <Container max className={cls.container}>
        <Row gutter={0} className={cls.wrapper} align="stretch">
          <Col xs={24} className={cls.wrapperCol}>
            <Header />
            <TableBlock
              dealer={dealer}
              loading={loading}
              setLoading={setLoading}
              result={exportResult}
              productsList={productsList}
              filteredProductsList={filteredProductsList}
              setFilteredProductsList={setFilteredProductsList}
              getList={getList}
              setResult={setExportResult} />
            <Footer
              dealerId={dealer.id}
              loading={loading}
              setLoading={setLoading}
              getList={getList}
              result={exportResult}
              setResult={setExportResult} />
          </Col>
        </Row>
      </Container>
    </SimpleForm>
  );
};
